// src/lib/proprio-api.js — Permissions 42 Phase 2 (CLIENT espace proprio)
//
// Client LECTURE SEULE des endpoints /api/beds24-mapping?action=proprio_*.
// Le rôle 'proprietaire' (co-hôte) accède aux données opérationnelles de SES
// logements UNIQUEMENT — le cloisonnement DUR est côté serveur (req.proprioLogIds
// = logIds du JWT signé). Ce client n'envoie JAMAIS de logIds de scope : il peut
// passer un logement_id de FILTRE, intersecté côté serveur (anti-IDOR).
// EXCEPTION UNIQUE à la lecture seule (LOT D, décision Dimitri 2026-08-01) :
// setProprioSignatureConsent (fin de fichier) — écriture volontaire bornée au
// consentement signature (table proprio_signature_consents côté serveur).
//
// Auth : Bearer JWT Supabase (même pattern que cfg-api.js / les autres clients).

import { supabase } from '../supabase'
import { lireJsonOuRefuser } from './reponse-json'

const ENDPOINT = '/api/beds24-mapping'

// ── « JE N'AI PAS PU LIRE » (2026-09-28) ───────────────────────────────────
// Même défaut que les blobs admin : `res.json().catch(() => ({}))` adoptait
// une page HTML servie en 200 (protection Vercel, portail captif) comme un
// « {} », et TOUT l'espace proprio s'ouvrait vide — calendrier, ménage,
// factures, statistiques — sans un mot. Ici le remède s'arrête au refus : les
// vues proprio montrent l'erreur (`ErrorBox message={state.error}`), donc la
// phrase atteint l'écran sans qu'on ait à poser un bandeau.
//
// ⚠️ « LES VUES MONTRENT L'ERREUR » A ÉTÉ VÉRIFIÉ UNE PAR UNE, et c'était FAUX
// pour l'une d'elles : `ProprioFicheView` (« Mon bien ») avalait tout dans un
// `.catch(() => setLogs({ list: [] }))` et affichait « Aucun logement
// rattaché. » — le refus partait bien et mourait avant l'écran. Corrigé dans le
// même lot. Toute NOUVELLE vue proprio doit porter son état d'erreur : sans
// lui, ce refus redevient un écran vide et plausible.
export const MESSAGE_PROPRIO_ILLISIBLE =
  "Je n'ai pas pu lire vos données : ce que le serveur a renvoyé n'en " +
  "était pas. Rien n'est perdu — rechargez la page."

// En-têtes auth (Bearer JWT). Pattern repris de src/lib/cfg-api.js.
async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// GET générique sur le routeur avec action + query params. Renvoie le JSON ou
// throw une Error lisible (consommée par les vues → état d'erreur propre).
async function get(action, params = {}) {
  const qs = new URLSearchParams({ action })
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') qs.set(k, String(v))
  }
  let res
  try {
    res = await fetch(`${ENDPOINT}?${qs.toString()}`, {
      method: 'GET',
      headers: await authHeaders(),
    })
  } catch (e) {
    // Erreur réseau — pas de PII dans le message.
    throw new Error('Connexion impossible. Réessayez.')
  }
  return lireJsonOuRefuser(res, `Erreur ${res.status}`, MESSAGE_PROPRIO_ILLISIBLE)
}

// (a) Calendrier / occupation de SES logements sur une plage [from, to] (ISO).
//   Renvoie { reservations, blocks }.
export function fetchProprioCalendar({ from, to, logementId } = {}) {
  return get('proprio_calendar', { from, to, logement_id: logementId })
}

// (a-bis) Grille tarifaire LECTURE SEULE de SES logements sur [from, to] (ISO),
//   pour le planning lane-Gantt. Renvoie { pricing } — chaque ligne
//   { logement_id, date, price1, min_stay }. JAMAIS is_manual_override (verrou
//   manuel = signal channel-manager interne, masqué côté serveur). Lecture seule.
export function fetchProprioPricingCalendar({ from, to, logementId } = {}) {
  return get('proprio_pricing_calendar', { from, to, logement_id: logementId })
}

// (b) Facturation : revenus + commission de mise en relation du mois (YYYY-MM).
//   Renvoie { month, logements, totals }. Aucun recalcul côté front (chemin argent).
export function fetchProprioFacturation({ month } = {}) {
  return get('proprio_facturation', { month })
}

// (c) Historique : factures du proprio (table invoices), optionnellement par année.
//   Renvoie { invoices }.
export function fetchProprioInvoices({ year } = {}) {
  return get('proprio_invoices', { year })
}

// (c-bis) Snapshot html_snapshot FIGÉ d'UNE facture du proprio (téléchargement /
//   impression). Cloisonné serveur (id ∧ logIds ; un id hors scope → 404). Renvoie
//   { invoice_number, html }. Lecture seule stricte — aucune régénération serveur.
export function fetchProprioInvoiceHtml({ id } = {}) {
  return get('proprio_invoice_html', { id })
}

// (d-bis) Années réelles pour les sélecteurs facturation. Renvoie
//   { invoiceYears, caYears } (DESC). Vide = aucune donnée (le front replie sur
//   l'année en cours — jamais un sélecteur vide).
export function fetchProprioFacturationYears() {
  return get('proprio_facturation_years')
}

// (e) Messages : conversations/messages des résas de SES logements (lecture).
//   Renvoie { messages }. PII voyageur conservée (co-hôte).
export function fetchProprioMessages({ logementId, limit } = {}) {
  return get('proprio_messages', { logement_id: logementId, limit })
}

// (f) Incidents : incidents V2 de SES logements (lecture). Renvoie { incidents }.
export function fetchProprioIncidents({ logementId, status, limit } = {}) {
  return get('proprio_incidents', { logement_id: logementId, status, limit })
}

// (g) Logements du proprio (id + nom seulement) pour « Ma fiche ». Renvoie { logements }.
export function fetchProprioLogements() {
  return get('proprio_logements')
}

// (h) Avis voyageurs de SES logements (lecture, DGCCRF). Renvoie
//   { avis, statsByLog, statsOverall }. Texte ORIGINAL voyageur, jamais les
//   notes internes FBZ (admin_notes/admin_text exclues côté serveur).
export function fetchProprioAvis({ logementId } = {}) {
  return get('proprio_avis', { logement_id: logementId })
}

// (j) Statistiques (performance/activité) de SES logements sur une période
//   [from, to] en 'YYYY-MM' (défaut serveur = 12 mois glissants si omis).
//   Renvoie { parMois, total, kpis, parPlateforme }. Champs « bruts » (CA brut,
//   commissions, taxe, voyageurs…) à null en KV (logement non V2) — le front
//   affiche « — ». Aucune commission FBZ / net proprio (masqués). Lecture seule.
export function fetchProprioStats({ from, to, logementId } = {}) {
  return get('proprio_stats', { from, to, logement_id: logementId })
}

// (i) Passages ménage de SES logements (lecture). Renvoie { runs } : date,
//   statut, checklist (items_checked), photos (signed URL bucket privé),
//   nb voyageurs. Champs internes (emails prestataire/tarif/notes/heures réelles
//   voyageur) exclus côté serveur.
export function fetchProprioMenage({ logementId } = {}) {
  return get('proprio_menage', { logement_id: logementId })
}

// ─────────────────────────────────────────────────────────────────────────
// LOT D — consentement signature (PREMIÈRE action proprio en ÉCRITURE,
// volontaire et STRICTEMENT bornée au consentement signature — le reste de
// l'espace proprio demeure lecture seule ; cf. lib/proprio-handlers.js).
// Le client n'envoie JAMAIS de proprio_id (dérivé du JWT côté serveur).
// ─────────────────────────────────────────────────────────────────────────

// POST générique (usage borné aux actions consentement). Même contrat d'erreur
// que get() : throw Error lisible, jamais de PII dans le message.
async function post(action, body = {}) {
  let res
  try {
    res = await fetch(`${ENDPOINT}?${new URLSearchParams({ action }).toString()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ action, ...body }),
    })
  } catch (e) {
    throw new Error('Connexion impossible. Réessayez.')
  }
  return lireJsonOuRefuser(res, `Erreur ${res.status}`, MESSAGE_PROPRIO_ILLISIBLE)
}

// (k) État du consentement signature. Renvoie { applicable, active,
//   accepted_at, consent_text, state }. FAIL-CLOSED côté UI : toute erreur /
//   état 'unknown' → case affichée DÉCOCHÉE.
export function fetchProprioSignatureConsent() {
  return get('proprio_signature_consent_get')
}

// (k-bis) Accepte (accept:true → nouvelle ligne horodatée IP/UA serveur) ou
//   révoque (accept:false → revoked_at sur la ligne active, trace conservée).
export function setProprioSignatureConsent(accept) {
  return post('proprio_signature_consent_set', { accept: !!accept })
}
